import type { MetricItem as Metric } from 'webapp-types';
import { OrderDirection } from 'webapp/enums/enums';
import { toSorted } from 'webapp/utils/common';

import type { Metadata } from 'webapp/models-custom/space';

const enum SortBy {
	POPULARITY = 'popularity',
	ALPHABETICALLY = 'alphabetically',
}

const AdvancedSortProps = {
	prop: 'advancedBlockMetricSort',
	direction: OrderDirection.ASC,
};

function getPositionByType(m: Metric) {
	let position = 0; // Basic metrics

	if (m.isTypeCustomQueryOrGeneric) {
		position = 1; // Custom metrics
	} else if (m.isTypeMetricCalculationOrGeneric) {
		position = 2; // Calculated metrics
	}

	return position;
}

function getPositionByOrigin(m: Metric) {
	let position: number;

	if (m.isPredefined) {
		position = 2;
	} else if (m.isTypeBasic || m.isGenericCustom) {
		position = 0;
	} else {
		position = 1; // user-created
	}

	return position;
}

function advancedSortFn(m1: Metric, m2: Metric) {
	/*
		Regroup metrics already sorted by popularity by the following section logic

		- Metrics from Connected Sources:
			- Basic metrics
				-----
				| - Generic metrics
				| - User created metrics (Example: User added metric in Token)
				| - Predefined Blocks (Multimetric)
				-----
				^ batched by sourceType (by popularity), then batched by sourceId
			- Custom metrics
				-----
				| - user created metrics (Example: User used QB in Klavio, or added custom predefined block to metrics / board)
				| - not yet used Custom Predefined Block
				-----
				^ batched by sourceType (by popularity), then batched by sourceId
			- Calculated metrics
				-----
				| - User created (User created manually, or added calculated predefined block to metrics / board)
				| - not yet used Calculated Predefined Block
				-----
				^ batched by sourceType (by popularity), then batched by sourceId
		- Metrics from Generic Sources:
			- <same as above, just ignore subsections that are not relevant>
	*/

	/* SORT BY Connected / Generic Source */
	const isConnectedM1 = !!m1.spaceAccessId,
		isConnectedM2 = !!m2.spaceAccessId;

	if (isConnectedM1 !== isConnectedM2) {
		return isConnectedM1 ? -1 : 1; // Move up if connected, move down if generic
	}
	// If both metrics are for connected, or both are for generic source, keep sorting

	/* SORT BY type: Basic, Custom, Calculated */
	const positionByTypeM1 = getPositionByType(m1),
		positionByTypeM2 = getPositionByType(m2);

	if (positionByTypeM1 !== positionByTypeM2) {
		return positionByTypeM1 < positionByTypeM2 ? -1 : 1;
	}
	// If both metrics are of same type, keep sorting

	/* SORT BY sourceType, by popularity */
	const positionBySourceTypePopularityM1 = m1.sourceType?.popularity || 999,
		positionBySourceTypePopularityM2 = m2.sourceType?.popularity || 999;

	if (positionBySourceTypePopularityM1 !== positionBySourceTypePopularityM2) {
		return positionBySourceTypePopularityM1 < positionBySourceTypePopularityM2 ? -1 : 1;
	}
	// If both metrics are of same type, keep sorting

	/* SORT BY (batch by) sourceId, if connected */
	// Based on the first sort - both are connected, or both are generic
	if (isConnectedM1 && m1.spaceAccessId !== m2.spaceAccessId) {
		const sourceIdM1 = m1.spaceAccessId,
			sourceIdM2 = m2.spaceAccessId!;

		return sourceIdM1 < sourceIdM2 ? 1 : -1; // Inverted, move less recent soures down
	}
	// If both metrics are for a generic source, or the same connected source, keep sorting

	/* SORT BY type: Generic, User, Predefined Block */
	const positionByOriginM1 = getPositionByOrigin(m1),
		positionByOriginM2 = getPositionByOrigin(m2);

	if (positionByOriginM1 !== positionByOriginM2) {
		return positionByOriginM1 < positionByOriginM2 ? -1 : 1;
	}
	// If both metrics are of same origin, keep sorting

	/* SORT BY popularity */
	const positionByPopularityM1 = m1.usedCount,
		positionByPopularityM2 = m2.usedCount;

	if (positionByPopularityM1 !== positionByPopularityM2) {
		return positionByPopularityM1 < positionByPopularityM2 ? 1 : -1; // Inverted, more up if usedCount is bigger
	}

	// If both same popularity, just sort alphabetically
	const metricNameM1 = (m1.name || '').toLowerCase(),
		metricNameM2 = (m2.name || '').toLowerCase();

	if (metricNameM1 !== metricNameM2) {
		return metricNameM1 > metricNameM2 ? 1 : -1;
	}

	return 0;
}

function prioritySortFn(metadata = {} as Metadata, sortBy: SortBy) {
	const { sourcesSelection = [], cloudSourcesOpportunity = [] } = metadata || {},
		opportunitySources = [...sourcesSelection, ...cloudSourcesOpportunity]; // Don't worry about duplicates

	return function (m1: Metric, m2: Metric) {
		// Most popular favorited metrics
		// General most popular metric from connected sources
		// General most popular metric from opportunity sources
		// General most popular metric overall (in case they don’t have a source connected)

		/* SORT BY favorite */
		const isFavoritedM1 = !!m1.isFavorite,
			isFavoritedM2 = !!m2.isFavorite;

		if (isFavoritedM1 !== isFavoritedM2) {
			return isFavoritedM1 ? -1 : 1; // Move up if isFavorited, move down if not
		}
		// If both metrics are favorited, or both are not, keep sorting

		/* SORT BY Connected / Generic Source */
		const isConnectedM1 = !!m1.spaceAccessId,
			isConnectedM2 = !!m2.spaceAccessId;

		if (isConnectedM1 !== isConnectedM2) {
			return isConnectedM1 ? -1 : 1; // Move up if connected, move down if generic
		}
		// If both metrics are for connected, or both are for generic source, keep sorting

		if (metadata) {
			/* SORT BY "belongs in opportunity", if not connected */
			const isOpportunityM1 = opportunitySources.includes(m1.dataSourceTypeKey),
				isOpportunityM2 = opportunitySources.includes(m2.dataSourceTypeKey);

			if (!isConnectedM1) {
				// Based on the previous sort - both are connected, or both are generic
				if (isOpportunityM1 !== isOpportunityM2) {
					return isOpportunityM1 ? -1 : 1; // Move up if desired, move down if not
				}
			}
			// If both metrics are from a connected source, or both are on desired / undesired unconnected source, keep sorting
		}

		if (sortBy === SortBy.POPULARITY) {
			/* SORT BY popularity */
			// Don't just return (m2.usedCount - m1.usedCount), return one of -1, 0, 1
			const positionByPopularityM1 = m1.usedCount,
				positionByPopularityM2 = m2.usedCount;

			if (positionByPopularityM1 !== positionByPopularityM2) {
				return positionByPopularityM1 < positionByPopularityM2 ? 1 : -1; // Inverted, more up if usedCount is bigger
			}
		} else if (sortBy === SortBy.ALPHABETICALLY) {
			/* SORT alphabetically BY source type key, source name, metric name */
			const sourceTypeKeyM1 = m1.dataSourceTypeKey,
				sourceTypeKeyM2 = m2.dataSourceTypeKey;

			if (sourceTypeKeyM1 !== sourceTypeKeyM2) {
				return sourceTypeKeyM1 > sourceTypeKeyM2 ? 1 : -1;
			}

			const sourceNameM1 = m1.sourceName,
				sourceNameM2 = m2.sourceName;

			if (sourceNameM1 !== sourceNameM2) {
				return sourceNameM1 > sourceNameM2 ? 1 : -1;
			}

			const metricNameM1 = (m1.name || '').toLowerCase(),
				metricNameM2 = (m2.name || '').toLowerCase();

			if (metricNameM1 !== metricNameM2) {
				return metricNameM1 > metricNameM2 ? 1 : -1;
			}
		}

		return 0; // Equally popular
	};
}

function sortByAdvanced(metrics: Metric[]) {
	return toSorted(metrics, advancedSortFn);
}

function sortByPopularityPriority(
	metrics: Metric[],
	metadata: Metadata,
	sortBy = SortBy.POPULARITY,
) {
	return toSorted(metrics, prioritySortFn(metadata, sortBy));
}

function sortByAlphabetically(metrics: Metric[], sortBy = SortBy.ALPHABETICALLY) {
	const fn = prioritySortFn(undefined, sortBy);
	return toSorted(metrics, fn);
}

function initialMetricSort(firstMetricItem: Metric, secondMetricItem: Metric): number {
	// favorites
	const isFirstMetricItemFavorited = firstMetricItem.isFavorite,
		isSecondMetricItemFavorited = secondMetricItem.isFavorite;

	if (isFirstMetricItemFavorited && !isSecondMetricItemFavorited) {
		return -1;
	} else if (!isFirstMetricItemFavorited && isSecondMetricItemFavorited) {
		return 1;
	}

	// source type name
	const isFirstMetricItemSourceTypeName = firstMetricItem.sourceType?.name || '',
		isSecondMetricItemSourceTypeName = secondMetricItem.sourceType?.name || '';

	if (isFirstMetricItemSourceTypeName !== isSecondMetricItemSourceTypeName) {
		return isFirstMetricItemSourceTypeName > isSecondMetricItemSourceTypeName ? 1 : -1;
	}

	// source name
	const isFirstMetricItemSourceName = firstMetricItem.sourceName,
		isSecondMetricItemSourceName = secondMetricItem.sourceName;

	if (isFirstMetricItemSourceName !== isSecondMetricItemSourceName) {
		return isFirstMetricItemSourceName > isSecondMetricItemSourceName ? 1 : -1;
	}

	// metric name
	const isFirstMetricItemName = firstMetricItem.name || '',
		isSecondMetricItemName = secondMetricItem.name || '';

	if (isFirstMetricItemName !== isSecondMetricItemName) {
		return isFirstMetricItemName > isSecondMetricItemName ? 1 : -1;
	}

	return 0;
}

function sortByConnectionStatus(firstMetricItem: Metric, secondMetricItem: Metric): number {
	// prioritize connected sources
	const isFirstMetricItemOnSource = firstMetricItem.isOnSource,
		isSecondMetricItemOnSource = secondMetricItem.isOnSource;

	if (isFirstMetricItemOnSource && !isSecondMetricItemOnSource) {
		return -1;
	} else if (!isFirstMetricItemOnSource && isSecondMetricItemOnSource) {
		return 1;
	}

	// sort by root source
	/*const isFirstMetricItemRootSource = firstMetricItem.source?.isRoot,
		isSecondMetricItemRootSource = secondMetricItem.source?.isRoot;

	if (isFirstMetricItemRootSource && !isSecondMetricItemRootSource) {
		return 1;
	} else if (!isFirstMetricItemRootSource && isSecondMetricItemRootSource) {
		return -1;
	}*/

	// move generic custom metrics down
	const isFirstMetricGenericCustom = firstMetricItem.isGenericCustom,
		isSecondMetricGenericCustom = secondMetricItem.isGenericCustom;

	if (isFirstMetricGenericCustom && !isSecondMetricGenericCustom) {
		return 1;
	} else if (!isFirstMetricGenericCustom && isSecondMetricGenericCustom) {
		return -1;
	}

	return 0;
}

export {
	AdvancedSortProps,
	advancedSortFn,
	sortByAdvanced,
	sortByPopularityPriority,
	sortByAlphabetically,
	initialMetricSort,
	sortByConnectionStatus,
};
